import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
/**
 * App-level ErrorBoundary. Wraps the React tree at the root in `main.tsx` so
 * that a render error anywhere in the app surfaces a recoverable fallback
 * instead of a blank screen.
 */
import * as React from "react";

export interface ErrorBoundaryProps {
  /** Short label rendered in the fallback (e.g. "puku-pcb"). */
  label?: string;
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error(`[${this.props.label ?? "app"}] render error`, error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  reload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  render(): React.ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background p-8 text-foreground">
        <div className="max-w-md space-y-4 rounded-lg border border-border bg-card p-6 text-card-foreground shadow-lg">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Something went wrong</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {this.props.label ? `${this.props.label} encountered an unrecoverable error. ` : ""}
            You can retry the current view, or reload the page to start fresh.
          </p>
          <pre className="max-h-48 overflow-auto rounded-md border border-border bg-muted p-3 text-xs text-foreground">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2">
            <Button onClick={this.reset} variant="default">
              Try again
            </Button>
            <Button onClick={this.reload} variant="outline">
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
